import {
  Stack,
  Text,
  Title,
  Button,
  Group,
  Paper,
  ActionIcon,
  Alert,
  TextInput,
  Menu,
  rem,
  Table,
  Transition,
  Select,
  Checkbox,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconSearch,
  IconAlertCircle,
  IconDotsVertical,
  IconCarrot,
  IconTagStarred,
  IconWorldStar,
  IconShoppingCart,
  IconShoppingCartOff,
  IconSquareCheck,
  IconSquareMinusFilled,
} from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '../../hooks/useAuth';
import { LoadingScreen } from '../Layout/LoadingScreen';
import { ProductPreferenceDrawer } from './ProductPreferenceDrawer';

export type ProductPreferenceScope = "global" | "item" | "category";

type ProductPreferenceCategory = 
  | "produce" 
  | "meat" 
  | "dairy" 
  | "pantry_dry_goods" 
  | "spices_seasonings" 
  | "frozen" 
  | "bakery" 
  | "beverages" 
  | "other";

type UserProductPreference = {
  id: string;
  scope: ProductPreferenceScope;
  itemName?: string;
  category?: ProductPreferenceCategory;
  customPreference?: string;
  createdAt: string;
  lastUpdated: string;
};

interface ProductPreferencesProps {
  onAddPreferenceRef?: (ref: () => void) => void;
}

export const ProductPreferences = ({ 
  onAddPreferenceRef
}: ProductPreferencesProps) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [preferences, setPreferences] = useState<UserProductPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [modalOpened, setModalOpened] = useState(false);
  const [editingPreference, setEditingPreference] = useState<UserProductPreference | undefined>(undefined);
  const [selectedPreferences, setSelectedPreferences] = useState<Set<string>>(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Values must match ProductPreferenceScope — preferences store 'item',
  // not 'ingredient'.
  const scopeOptions = [
    { value: 'all', label: 'All preferences' },
    { value: 'item', label: 'Specific ingredients' },
    { value: 'category', label: 'Categories' },
    { value: 'global', label: 'Everything' },
  ];

  const categories: { value: string; label: string }[] = [
    { value: 'produce', label: 'Produce' },
    { value: 'meat', label: 'Meat' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'pantry_dry_goods', label: 'Pantry & dry goods' },
    { value: 'spices_seasonings', label: 'Spices & seasonings' },
    { value: 'frozen', label: 'Frozen' },
    { value: 'bakery', label: 'Bakery' },
    { value: 'beverages', label: 'Beverages' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);
        
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/product-preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch product preferences');
        }

        const data = await response.json();
        setPreferences(data.preferences || []);
      } catch (error: any) {
        console.error('Error fetching product preferences:', error);
        setError(error.message || 'Failed to load product preferences');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [user]);

  const handleDeletePreference = async (preferenceId: string) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/product-preferences/${preferenceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete preference');
      }

      setPreferences(prev => prev.filter(p => p.id !== preferenceId));
      
      // Remove from selected items
      setSelectedPreferences(prev => {
        const newSet = new Set(prev);
        newSet.delete(preferenceId);
        return newSet;
      });
      
      notifications.show({
        title: 'Success',
        message: 'Shopping preference deleted',
        color: 'green',
      });
    } catch (error: any) {
      console.error('Error deleting preference:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete preference. Please try again.',
        color: 'red',
      });
    }
  };

  const handleBulkDelete = async () => {
    if (!user || selectedPreferences.size === 0) return;

    setBulkDeleteLoading(true);
    
    const selectedIds = Array.from(selectedPreferences);
    
    // Clear selection
    setSelectedPreferences(new Set());
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/product-preferences`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          preferenceIds: selectedIds,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete preferences');
      }

      const result = await response.json();
      
      // Remove successfully deleted preferences from state
      const successfulIds = result.results
        .filter((r: any) => r.success)
        .map((r: any) => r.id);
      
      setPreferences(prev => prev.filter(p => !successfulIds.includes(p.id)));
      
      if (result.success) {
        notifications.show({
          title: 'Success',
          message: `Deleted ${result.summary.successful} preference${result.summary.successful === 1 ? '' : 's'}`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Partial success',
          message: `${result.summary.successful} deleted, ${result.summary.failed} failed. Please try again.`,
          color: 'yellow',
        });
      }
    } catch (error: any) {
      console.error('Error bulk deleting preferences:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete preferences. Please try again.',
        color: 'red',
      });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleSelectPreference = (preferenceId: string, checked: boolean) => {
    setSelectedPreferences(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(preferenceId);
      } else {
        newSet.delete(preferenceId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedPreferences(new Set());
  };

  const handleAddPreference = () => {
    setEditingPreference(undefined);
    setModalOpened(true);
  };

  // Pass the add preference handler to parent
  useEffect(() => {
    if (onAddPreferenceRef) {
      onAddPreferenceRef(handleAddPreference);
    }
  }, [onAddPreferenceRef]);

  const handleEditPreference = (preference: UserProductPreference) => {
    setEditingPreference(preference);
    setModalOpened(true);
  };

  const handleSavePreference = async (savedPreference: UserProductPreference) => {
    if (editingPreference) {
      // Update existing preference
      setPreferences(prev => prev.map(p =>
        p.id === savedPreference.id ? savedPreference : p
      ));
    } else {
      // Add new preference
      setPreferences(prev => [...prev, savedPreference]);
    }
    setModalOpened(false);
    
    // Optionally refetch to ensure we have the latest data
    if (user) {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/product-preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setPreferences(data.preferences || []);
        }
      } catch (error) {
        console.error('Error refetching preferences:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setEditingPreference(undefined);
  };

  const getScopeLabel = (preference: UserProductPreference) => {
    switch (preference.scope) {
      case 'global':
        return 'Everything';
      case 'category':
        return categories.find(c => c.value === preference.category)?.label || preference.category;
      case 'item':
        return preference.itemName;
      default:
        return preference.scope;
    }
  };

  const filteredPreferences = preferences.filter(preference => {
    const matchesSearch = 
      (preference.customPreference?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (preference.itemName?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (preference.category?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesScope = scopeFilter === 'all' || preference.scope === scopeFilter;
    
    return matchesSearch && matchesScope;
  });

  // Group preferences by scope
  const groupedPreferences = {
    global: filteredPreferences.filter(p => p.scope === 'global'),
    category: filteredPreferences.filter(p => p.scope === 'category'),
    ingredient: filteredPreferences.filter(p => p.scope === 'item'),
  };

  const hasPreferences = preferences.length > 0;
  const filtersActive = !!searchQuery || scopeFilter !== 'all';

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Stack gap="xl">
      {/* Error Message */}
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          variant="light"
          radius="lg"
        >
          {error}
        </Alert>
      )}

      {/* Search and Filter — only when there are preferences to search */}
      {hasPreferences && (
      <Paper p="md" radius="lg" withBorder>
        <Group gap="md" align="end">
          <TextInput
            placeholder="Search preferences..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            style={{ flex: 1 }}
            maw={{ base: '100%', sm: 400 }}
          />
          <Select
            placeholder="Filter by scope"
            value={scopeFilter}
            onChange={(value) => setScopeFilter(value || 'all')}
            data={scopeOptions}
            w={{ base: '100%', sm: 180 }}
          />
        </Group>
      </Paper>
      )}

      {/* Bulk Actions Toolbar */}
      <Transition
        mounted={selectedPreferences.size > 0}
        transition="slide-down"
        duration={200}
      >
        {(styles) => (
          <Paper
            p="sm"
            radius="lg"
            withBorder
            style={styles}
            bg="var(--mantine-color-gray-0)"
          >
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={clearSelection}
                  title="Clear selection"
                >
                  <IconSquareMinusFilled size={20} />
                </ActionIcon>
                <Text size="sm" c="dimmed">
                  {selectedPreferences.size} selected
                </Text>
              </Group>
              <Button
                color="red"
                variant="light"
                size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={handleBulkDelete}
                loading={bulkDeleteLoading}
              >
                Delete
              </Button>
            </Group>
          </Paper>
        )}
      </Transition>

      {/* Empty State */}
      {filteredPreferences.length === 0 && !loading && (
        hasPreferences ? (
          /* Preferences exist but the search/filter matches none of them */
          <Paper p="xl" radius="lg" withBorder>
            <Stack gap="md" align="center">
              <IconShoppingCartOff size={48} color="var(--mantine-color-gray-5)" />
              <Text fw={500} ta="center">
                No preferences found matching your filters
              </Text>
              <Text c="dimmed" size="sm" ta="center">
                Try adjusting your search or filter criteria
              </Text>
              {filtersActive && (
                <Button
                  variant="light"
                  onClick={() => {
                    setSearchQuery('');
                    setScopeFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          /* True zero state: explain what shopping preferences do */
          <Paper p="xl" radius="lg" withBorder>
            <Stack gap="lg" align="center" py="md">
              <IconShoppingCart size={48} color="var(--mantine-color-gray-5)" />
              <Stack gap="xs" align="center">
                <Title order={3} ta="center">
                  Tell us how you like to shop
                </Title>
                <Text c="dimmed" size="sm" ta="center" maw={460}>
                  Preferences guide which products we pick when filling your
                  Kroger cart.
                </Text>
                <Text c="dimmed" size="xs" ta="center" maw={460}>
                  e.g. "organic when available" or "store brand for dairy"
                </Text>
              </Stack>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleAddPreference}
              >
                Add your first preference
              </Button>
            </Stack>
          </Paper>
        )
      )}

      {/* Global Preferences Section */}
      {(scopeFilter === 'all' || scopeFilter === 'global') && groupedPreferences.global.length > 0 && (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="xs" align="center">
              <IconWorldStar size={20} />
              <Stack gap={0}>
                <Text fw={600} size="lg">Everything</Text>
                <Text size="sm" c="dimmed">Default preferences for all shopping</Text>
              </Stack>
            </Group>
          </Group>
          
          <Paper radius="lg" withBorder>
            <Table>
              <Table.Tbody>
                {groupedPreferences.global.map(preference => (
                  <Table.Tr key={preference.id}>
                    <Table.Td style={{ width: '40px' }}>
                      <Checkbox
                        size="sm"
                        checked={selectedPreferences.has(preference.id)}
                        onChange={(e) => handleSelectPreference(preference.id, e.currentTarget.checked)}
                        styles={{
                          input: { cursor: 'pointer' },
                          label: { cursor: 'pointer' }
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ fontStyle: 'italic' }}>
                        {preference.customPreference}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ width: '60px' }}></Table.Td>
                    <Table.Td style={{ width: '60px' }}>
                      <Menu shadow="md" width={160}>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="lg">
                            <IconDotsVertical size={20} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item 
                            leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => handleEditPreference(preference)}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item 
                            leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                            color="red"
                            onClick={() => handleDeletePreference(preference.id)}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Category Preferences Section */}
      {(scopeFilter === 'all' || scopeFilter === 'category') && groupedPreferences.category.length > 0 && (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="xs" align="center">
              <IconTagStarred size={20} />
              <Stack gap={0}>
                <Text fw={600} size="lg">Categories</Text>
                <Text size="sm" c="dimmed">Preferences for entire food categories</Text>
              </Stack>
            </Group>
            <>
              {isMobile ? (
                <ActionIcon
                  variant="light"
                  color="orange"
                  size="sm"
                  onClick={() => {
                    setEditingPreference({ scope: 'category' } as any);
                    setModalOpened(true);
                  }}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              ) : (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => {
                    setEditingPreference({ scope: 'category' } as any);
                    setModalOpened(true);
                  }}
                >
                  Add Category
                </Button>
              )}
            </>
          </Group>
          
          <Paper radius="lg" withBorder>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '40px' }}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      mt={4}
                      onClick={() => {
                        const allSelected = groupedPreferences.category.every(p => selectedPreferences.has(p.id));
                        if (allSelected) {
                          setSelectedPreferences(prev => {
                            const newSet = new Set(prev);
                            groupedPreferences.category.forEach(p => newSet.delete(p.id));
                            return newSet;
                          });
                        } else {
                          setSelectedPreferences(prev => {
                            const newSet = new Set(prev);
                            groupedPreferences.category.forEach(p => newSet.add(p.id));
                            return newSet;
                          });
                        }
                      }}
                      title="Select all category preferences"
                    >
                      {groupedPreferences.category.every(p => selectedPreferences.has(p.id)) && groupedPreferences.category.length > 0 ? (
                        <IconSquareMinusFilled size={20} />
                      ) : (
                        <IconSquareCheck size={20} />
                      )}
                    </ActionIcon>
                  </Table.Th>
                  <Table.Th style={{ width: '30%' }}>Category</Table.Th>
                  <Table.Th>Preference</Table.Th>
                  <Table.Th style={{ width: '60px' }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groupedPreferences.category.map(preference => (
                  <Table.Tr key={preference.id}>
                    <Table.Td>
                      <Checkbox
                        size="sm"
                        checked={selectedPreferences.has(preference.id)}
                        onChange={(e) => handleSelectPreference(preference.id, e.currentTarget.checked)}
                        styles={{
                          input: { cursor: 'pointer' },
                          label: { cursor: 'pointer' }
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {getScopeLabel(preference)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ fontStyle: 'italic' }}>
                        {preference.customPreference}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" width={160}>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="lg">
                            <IconDotsVertical size={20} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item 
                            leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => handleEditPreference(preference)}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item 
                            leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                            color="red"
                            onClick={() => handleDeletePreference(preference.id)}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Ingredient Preferences Section */}
      {(scopeFilter === 'all' || scopeFilter === 'item') && groupedPreferences.ingredient.length > 0 && (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="xs" align="center">
              <IconCarrot size={20} />
              <Stack gap={0}>
                <Text fw={600} size="lg">Specific ingredients</Text>
                <Text size="sm" c="dimmed">Preferences for individual ingredients</Text>
              </Stack>
            </Group>
            <>
              {isMobile ? (
                <ActionIcon
                  variant="light"
                  color="orange"
                  size="sm"
                  onClick={() => {
                    setEditingPreference({ scope: 'item' } as any);
                    setModalOpened(true);
                  }}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              ) : (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => {
                    setEditingPreference({ scope: 'item' } as any);
                    setModalOpened(true);
                  }}
                >
                  Add ingredient
                </Button>
              )}
            </>
          </Group>
          
          <Paper radius="lg" withBorder>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '40px' }}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      mt={4}
                      onClick={() => {
                        const allSelected = groupedPreferences.ingredient.every(p => selectedPreferences.has(p.id));
                        if (allSelected) {
                          setSelectedPreferences(prev => {
                            const newSet = new Set(prev);
                            groupedPreferences.ingredient.forEach(p => newSet.delete(p.id));
                            return newSet;
                          });
                        } else {
                          setSelectedPreferences(prev => {
                            const newSet = new Set(prev);
                            groupedPreferences.ingredient.forEach(p => newSet.add(p.id));
                            return newSet;
                          });
                        }
                      }}
                      title="Select all ingredient preferences"
                    >
                      {groupedPreferences.ingredient.every(p => selectedPreferences.has(p.id)) && groupedPreferences.ingredient.length > 0 ? (
                        <IconSquareMinusFilled size={20} />
                      ) : (
                        <IconSquareCheck size={20} />
                      )}
                    </ActionIcon>
                  </Table.Th>
                  <Table.Th style={{ width: '30%' }}>Ingredient</Table.Th>
                  <Table.Th>Preference</Table.Th>
                  <Table.Th style={{ width: '60px' }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groupedPreferences.ingredient.map(preference => (
                  <Table.Tr key={preference.id}>
                    <Table.Td>
                      <Checkbox
                        size="sm"
                        checked={selectedPreferences.has(preference.id)}
                        onChange={(e) => handleSelectPreference(preference.id, e.currentTarget.checked)}
                        styles={{
                          input: { cursor: 'pointer' },
                          label: { cursor: 'pointer' }
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {preference.itemName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ fontStyle: 'italic' }}>
                        {preference.customPreference}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" width={160}>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="lg">
                            <IconDotsVertical size={20} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item 
                            leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => handleEditPreference(preference)}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item 
                            leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                            color="red"
                            onClick={() => handleDeletePreference(preference.id)}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Add/Edit Drawer */}
      <ProductPreferenceDrawer
        opened={modalOpened}
        onClose={handleCloseModal}
        preference={editingPreference}
        onSave={handleSavePreference}
      />
    </Stack>
  );
};
